/**
 * Batch 18-D6 — Confidence Tier Multipliers.
 *
 * Single source of truth for the per-confidence-level multiplier
 * values used by training-recommendation scoring. The values had
 * previously been duplicated as inline literals in
 * `contextualTrainingScorer.ts` (applied multiplicatively into the
 * final score) and `trainingRecommendationEngine.ts` (applied
 * additively via `factors.confidenceBoost`). 18-D6 dedupes the
 * SOURCE VALUES only — each scorer's call site continues to apply
 * them with its own math.
 *
 * Values are preserved BIT-FOR-BIT from the prior live constants
 * (the audit confirmed all three duplicates carried identical
 * numbers):
 *
 *   provisional         1.0
 *   community_validated 1.2
 *   optibay_verified    1.5
 *
 * Type discipline:
 *   - The exported constant is typed against the scaffold's
 *     `TrainingConfidenceMultipliers` (= `Record<ConfidenceLevel, number>`),
 *     which is narrower than the `Record<string, number>` both
 *     scorers used locally. Under the project's `strictNullChecks:
 *     false` config the narrower type is structurally compatible
 *     with both scorers' existing call sites (the values are looked
 *     up by `artifact.confidence_level: ConfidenceLevel`), so the
 *     swap is type-safe and runtime-identical.
 *
 *   - Each scorer's call site keeps its own `|| 1.0` fallback so the
 *     observable lookup-miss behavior is preserved exactly. The
 *     fallback is defensive: with a total `Record<ConfidenceLevel, number>`
 *     it can only fire for runtime values outside the declared
 *     `ConfidenceLevel` union.
 *
 * Out of scope for 18-D6:
 *   - `src/hooks/useAdaptiveRecommendations.ts:184` also carries a
 *     duplicate of these values, applied additively. It is NOT
 *     migrated in this batch; the prompt scope explicitly lists only
 *     the two scorer files. Deferred to a future batch (18-D7
 *     candidate) so it can be reviewed alongside the hook's tests.
 *   - The multiplicative vs additive application difference between
 *     scorers is INTENTIONAL today; this batch does not unify the
 *     math.
 */

import type { ConfidenceLevel } from '@/types/training';
import type { TrainingConfidenceMultipliers } from '@/utils/training/scoring/types';

// ─────────────────────────────────────────────────────────────────────
// Canonical multiplier table
//
// Order of keys matches the historical contextualTrainingScorer
// declaration (verified-first). Insertion order is not load-bearing
// for either consumer — both look up by key — but is preserved for
// reviewer continuity.
// ─────────────────────────────────────────────────────────────────────

export const CONFIDENCE_MULTIPLIERS: TrainingConfidenceMultipliers = {
  optibay_verified: 1.5,
  community_validated: 1.2,
  provisional: 1.0,
};

// ─────────────────────────────────────────────────────────────────────
// Lookup helper
//
// Returns the multiplier for a given confidence level. Falls back to
// the `provisional` value (1.0) for null / undefined / unknown
// strings — the same fallback both current scorers use via their
// `|| 1.0` clause. Exported for future surfaces; existing scorers
// continue to use the exported constant directly to preserve their
// in-place call shapes.
// ─────────────────────────────────────────────────────────────────────

export function getConfidenceMultiplier(
  level: ConfidenceLevel | null | undefined,
): number {
  if (!level) return CONFIDENCE_MULTIPLIERS.provisional;
  return CONFIDENCE_MULTIPLIERS[level] ?? CONFIDENCE_MULTIPLIERS.provisional;
}
